/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {Injectable} from '@angular/core';
import {FormFieldAttribute} from '../models/FormCategory';
import {EXTERNAL_ROLES} from '../models/access-request.dto';

/**
 * Known role suffixes used to derive the translation label from a fully
 * qualified role name (e.g. `BP_SIEMENS_PERSON_INTEREST_RECYCLER`).
 */
const KNOWN_ROLE_SUFFIXES = [
  'DATA_MANAGER',
  'DATA_PROVIDER',
  'SYS_ADMIN',
  'GLOBAL_SETUP_ROLE',
  ...EXTERNAL_ROLES,
];

@Injectable({
  providedIn: 'root',
})
export class GeneralDataHandlingService {
  getNestedKeys(obj: any, parentKey = ''): string[] {
    let result: string[] = [];

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        const fullKey = `${parentKey}${key}`;

        if (Array.isArray(value)) {
          value.forEach((item, index) => {
            const arrayKey = `${fullKey}[${index}]`;
            result = result.concat(
              typeof item === 'object' && item !== null
                ? this.getNestedKeys(item, `${arrayKey}.`)
                : arrayKey,
            );
          });
        } else if (typeof value === 'object' && value !== null) {
          result = result.concat(this.getNestedKeys(value, `${fullKey}.`));
        } else {
          result.push(fullKey);
        }
      }
    }

    return result;
  }

  public setValueByPath(
    obj: any,
    path: string,
    value?: string | number | unknown,
  ): void {
    const keys = path.split(/[.\[\]'"]/).filter(Boolean);
    return keys.reduce((acc, key, index) => {
      if (index === keys.length - 1) {
        acc[key] = value;
      } else {
        acc[key] = acc[key] || (isNaN(Number(keys[index + 1])) ? {} : []);
      }
      return acc[key];
    }, obj);
  }

  public getValueByPath(obj: unknown, path: string): any | null {
    return path
      .split(/[.\[\]'"]/)
      .filter(Boolean)
      .reduce((acc: any, key) => acc?.[key], obj);
  }

  /**
   * This function searches through a nested data structure
   * to find all objects that have a specific attribute with a given value.
   * It recursively traverses the data, checking each object for the specified attribute.
   * If the attribute matches the search term, the object is added to the results array.
   * @param data
   * @param searchTerm
   * @param attributeName
   */
  public findAttributeWithValue(
    data: unknown,
    searchTerm: string,
    attributeName: string,
  ): FormFieldAttribute[] {
    const results: FormFieldAttribute[] = [];

    function searchRecursively(obj: any): void {
      if (Array.isArray(obj)) {
        obj.forEach((item) => searchRecursively(item));
        return;
      }

      if (obj && typeof obj === 'object') {
        if (obj[attributeName] === searchTerm) {
          results.push(obj);
          return;
        }

        Object.keys(obj).forEach((key) => {
          const value = obj[key];
          if (Array.isArray(value) || (value && typeof value === 'object')) {
            searchRecursively(value);
          }
        });
      }
    }

    searchRecursively(data);
    return results;
  }

  /**
   * Checks if any attribute in the provided dataList is set.
   * If at least one attribute is set (not null, undefined, or empty),
   * it returns true. Otherwise, it returns false.
   * @param data
   */
  public atLeastOneFieldSetIn(data: unknown[] | unknown): boolean {
    // Base Case 1: If the value itself is a primitive and not null/undefined, it's "set".
    // This covers strings, numbers, booleans, and symbols.
    // Note: typeof null is 'object', so we explicitly check for null.
    if (
      data !== null &&
      typeof data !== 'undefined' &&
      typeof data !== 'object'
    ) {
      return true;
    }

    // Base Case 2: If the value is explicitly null or undefined, it's not set.
    if (data === null || typeof data === 'undefined') {
      return false;
    }

    // Recursive Case 1: If it's an array, iterate through its elements.
    // Use .some() for conciseness: if any element is "set" recursively, return true.
    if (Array.isArray(data)) {
      return data.some((item) => this.atLeastOneFieldSetIn(item));
    }

    // Recursive Case 2: If it's a plain object, iterate through its property values.
    // Object.values() gets an array of values for all enumerable own properties.
    // Use .some(): if any property value is "set" recursively, return true.
    // This implicitly handles the dynamic attribute names and `hasOwnProperty`.
    if (typeof data === 'object') {
      // This catches plain objects, but also Dates, RegExps etc.
      // For this problem, we assume plain objects or arrays.
      return Object.values(data).some((value) =>
        this.atLeastOneFieldSetIn(value),
      );
    }

    // Fallback for any other unexpected types (e.g., functions, custom classes not handled above)
    // If we reach here, and it's not null/undefined, it's considered "set".
    return true;
  }

  /**
   * Returns the role label based on the provided role string, by checking
   * specific suffixes. If no match is found, it returns the original role string.
   * @param role - The role string to evaluate.
   * @returns The corresponding role label or the original role string if no match is found.
   */
  public static returnRoleLabel(role: string): string {
    return KNOWN_ROLE_SUFFIXES.find((suffix) => role.endsWith(suffix)) ?? role;
  }
}
